import { getDays, getHours, getMinutes, getSeconds, hasDays } from '@core/shared';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';

export const UpNextWebinarCountdown = ({ broadcastDateTime: broadcastDateTimeStr, now }: { broadcastDateTime: string; now: DateTime }) => {
    const { t } = useTranslation();
    const [webinarCountDownTime, setWebinarCountDownTime] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const language = getI18n().language;

    const broadcastDateTime = useMemo(() => {
        return DateTime.fromISO(broadcastDateTimeStr).toLocal().setLocale(language);
    }, [broadcastDateTimeStr, language]);

    useEffect(() => {
        if (!broadcastDateTime) return;

        const webinarDate = new Date(broadcastDateTimeStr).getTime();

        const updateCountDown = () => {
            const todayDate = new Date().getTime();
            const timeDifference = webinarDate - todayDate;

            if (timeDifference <= 0) {
                setWebinarCountDownTime(0);
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
                return;
            }
            setWebinarCountDownTime(timeDifference > 0 ? timeDifference : 0);
        };

        updateCountDown();

        intervalRef.current = setInterval(updateCountDown, 1000);
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [broadcastDateTime]);

    return (
        <div className="bg-white border-2 border-white rounded-full flex items-baseline gap-x-2 px-3 py-0.5">
            {now > broadcastDateTime ? (
                <span className="uppercase animate-pulse">{t('Webinar.DateTime.StartNow')}</span>
            ) : (
                <>
                    {t('Webinar.DateTime.StartIn')}
                    <div className="flex gap-0.5 items-center font-medium text-xl leading-none" role="timer" aria-atomic="true" aria-live="polite">
                        {hasDays(webinarCountDownTime) && (
                            <>
                                <div key="days" className="flex flex-col">
                                    <div className="flex [&>span]:w-[.6667em]">
                                        {getDays(webinarCountDownTime)
                                            .split('')
                                            .map((dayNumber, index) => (
                                                <span key={`hourNumber${index}`}>{dayNumber}</span>
                                            ))}
                                    </div>
                                </div>
                                <div>:</div>
                            </>
                        )}
                        <div className="flex flex-col items-center">
                            <div className="flex [&>span]:w-[.6667em]">
                                {getHours(webinarCountDownTime)
                                    .split('')
                                    .map((hourNumber, index) => (
                                        <span key={`hourNumber${index}`}>{hourNumber}</span>
                                    ))}
                            </div>
                        </div>
                        <div>:</div>
                        <div className="flex flex-col items-center">
                            <div className="flex [&>span]:w-[.6667em]">
                                {getMinutes(webinarCountDownTime)
                                    .split('')
                                    .map((minuteNumber, index) => (
                                        <span key={`minuteNumber${index}`}>{minuteNumber}</span>
                                    ))}
                            </div>
                        </div>
                        <div>:</div>
                        <div className="flex flex-col items-center">
                            <div className="flex [&>span]:w-[.6667em]" {...(webinarCountDownTime > 10000 && { 'aria-hidden': 'true' })}>
                                {getSeconds(webinarCountDownTime)
                                    .split('')
                                    .map((secondNumber, index) => (
                                        <span key={`secondNumber${index}`}>{secondNumber}</span>
                                    ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
